<template>
  <section class="pricing-overview">
    <section class="price-group tarif-group">
      <toggle-group :start-open="false" :toggle-label="`Stroom (${pricing.electricity_consumption_total} kWh)`"
        :price="pricing.electricity_total_with_tax_discount" @open="
          logAnalyticsEvent(
            'compare-energy.open-price-calculation-electricity',
            analyticsProduct
          )
          ">
        <template #toggle-content>
          <price-row v-for="spec in pricing.electricity_specification" :key="spec.type"
            :label="specificationLabel(spec)" :calculation="spec.amount !== null ? `${spec.amount} kWh x ${toCurrencyAccuracy(spec.tariff, 5)} = ` : null
              " :price="spec.total" />

          <price-row label="Vaste leveringskosten per jaar" :price="pricing.electricity_base" />

          <price-row :label="`Netbeheerkosten (${pricing.distributor_name})`"
            :price="pricing.electricity_network_costs" />
          <price-row label="Vermindering energiebelasting" :price="-pricing.tax_discount" />
          <price-row type="bold-subtle" label="Totaal stroom" :price="pricing.electricity_total_with_tax_discount" />
        </template>
      </toggle-group>
      <toggle-group v-if="orderStore.product.gas_included" :start-open="false"
        :toggle-label="`Gas (${pricing.gas_consumption} m³)`" :price="pricing.gas_total" @open="
          logAnalyticsEvent(
            'compare-energy.open-price-calculation-gas',
            analyticsProduct
          )
          ">
        <template #toggle-content>
          <price-row v-for="spec in pricing.gas_specification" :key="spec.type" :label="specificationLabel(spec)"
            :calculation="`${spec.amount} m³ x ${toCurrencyAccuracy(spec.tariff, 5)} = `
              " :price="spec.total" />
          <price-row :label="`Netbeheerkosten (${pricing.distributor_name})`" :price="pricing.gas_network_costs" />
          <price-row label="Vaste leveringskosten per jaar" :price="pricing.gas_base" />
          <price-row type="bold-subtle" label="Totaal gas" :price="pricing.gas_total" />
        </template>
      </toggle-group>

      <ul class="default-price-rows">
        <toggle-group
          v-if="orderStore.product.gas_included && pricing.total_discount"
          toggle-label="Korting"
          :price="-pricing.total_discount"
          price-type="positive"
          :start-open="false"
          class="clean"
        >
          <template #toggle-content>
            <price-row
              v-if="orderStore.product.split_discounts"
              label="Korting op gas"
              :price="-pricing.total_discount_gas"
            />
            <price-row
              v-if="orderStore.product.split_discounts"
              label="Korting op stroom"
              :price="-pricing.total_discount_electricity"
            />
            <price-row
              type="bold"
              label="Totale korting"
              :price="-pricing.total_discount"
            />
            <li
              v-if="orderStore.product.gas_included"
              class="price-row"
            >
              <span class="label note">
                De korting wordt verrekend aan het einde van je contractperiode.
                Je kunt daarna je contract verlengen of overstappen naar een andere leverancier.
              </span>
            </li>
          </template>
        </toggle-group>
        <price-row
          v-else-if="pricing.total_discount"
          type="positive"
          label="Korting"
          :price="-pricing.total_discount"
        />
        <price-row
          v-else
          type="positive"
          :label="orderStore.product.discount_text"
        />
        <price-row
          label="Totaal per jaar"
          type="highlight"
          :price="orderStore.product.year_price"
        />
      </ul>
    </section>

    <section class="price-group">
      <ul class="monthly-price-rows">
        <price-row label="Gemiddelde maandprijs" :price="orderStore.product.month_price" />

        <toggle-group toggle-label="Bekijk tarieven">
          <template #toggle-content>
            <price-row label="Termijnbedrag" :price="orderStore.product.month_price_without_discount" />

            <price-row v-if="!doubleMeter" label="Tarief per kWh" :price="pricing.electricity_unit_price"
              format="long" />

            <price-row v-if="doubleMeter" label="Normaaltarief per kWh" :price="pricing.electricity_unit_price"
              format="long" />

            <price-row v-if="doubleMeter" label="Daltarief per kWh" :price="pricing.electricity_unit_price_off_peak"
              format="long" />

            <price-row v-if="electricityGenerated" label="Teruglevertarief per kWh"
              :price="pricing.electricity_surplus_tariff" format="long" />

            <price-row v-if="orderStore.product.gas_included" label="Gastarief per m³" :price="pricing.gas_unit_price"
              format="long" />

            <li class="price-row" :class="this.orderStore.product.electricity_green">
              <span class="label">
                {{ energySourceLabel }}&nbsp;
                <icon class="toggle-icon" icon="leaf-solid" size="sm" />
              </span>
            </li>
          </template>
        </toggle-group>

        <li v-if="vatDisclaimer" class="price-row disclaimer">
          <span class="label note">
            Deze tarieven zijn inclusief {{ orderStore.product.price_calculation.vat_percentage }}% btw.
            Het maandelijkse bedrag kun je later veranderen bij {{ orderStore.product.provider_name }}.
          </span>
        </li>
      </ul>
    </section>
  </section>
</template>

<script>
import Icon from '../../../vue-elements/icon/Icon.vue';
import PriceRow from './pricingOverview/PriceRow.vue';
import ToggleGroup from './pricingOverview/ToggleGroup.vue';
import { useOrderStore } from '../stores/orderStore.js';
import { useEnergyComparisonStore } from '../stores/energyComparison.js';

export default {
  components: {
    Icon,
    PriceRow,
    ToggleGroup,
  },
  props: {
    vatDisclaimer: Boolean,
  },
  setup() {
    const orderStore = useOrderStore();
    const comparisonStore = useEnergyComparisonStore();

    return {
      orderStore,
      comparisonStore,
    };
  },
  computed: {
    analyticsProduct() {
      // Object with basic product information to supply with analytics events
      return {
        provider_name: this.orderStore.product.provider_name,
        product_id: this.orderStore.product.base_id,
        rank: this.orderStore.product.rank,
        contract_duration: this.orderStore.product.contract_duration,
      };
    },
    pricing() {
      // get() {
      return this.orderStore.product.price_calculation;
      // },
    },
    doubleMeter() {
      return this.comparisonStore.filters.doubleMeter;
    },
    electricityGenerated() {
      return this.comparisonStore.filters.electricityGenerated;
    },
    tariffLabel() {
      return this.orderStore.product.gas_included
        ? 'Bekijk tarieven per kWh en m³'
        : 'Bekijk energietarieven per kWh';
    },
    energySourceLabel() {
      switch (this.orderStore.product.electricity_green) {
        case 'compensated':
          return 'Groene stroom uit Europa';
        case 'nl':
          return 'Groene stroom uit Nederland';
        case 'gray':
          return 'Grijze stroom';
      }
    },
    // energySources() {
    //   return this.orderStore.product.electricity_source.filter((electricitySource) => {
    //     return !!electricitySource.percentage;
    //   });
    // },
  },
  methods: {
    specificationLabel(specItem, unit) {
      switch (specItem.type) {
        case 'usage':
          return 'Variabele kosten';
        case 'usage_single':
          return 'Variabele kosten (enkel)';
        case 'usage_normal':
          return 'Variabele kosten (normaal)';
        case 'usage_offpeak':
          return 'Variabele kosten (dal)';
        case 'settling_single':
          return 'Salderen';
        case 'settling_normal':
          return 'Salderen (normaal)';
        case 'settling_offpeak':
          return 'Salderen (dal)';
        case 'feed_in':
          return 'Terugleveren';
        case 'feed_in_normal':
          return 'Terugleveren (normaal)';
        case 'feed_in_offpeak':
          return 'Terugleveren (dal)';
        case 'price_cap_electricity_single':
          return 'Variabele kosten (prijsplafond)';
        case 'price_cap_electricity_normal':
          return 'Variabele kosten (prijsplafond, normaal)';
        case 'price_cap_electricity_offpeak':
          return 'Variabele kosten (prijsplafond, dal)';
        case 'price_cap_gas':
          return 'Variabele kosten (prijsplafond)';
        case 'settling_price_cap':
          return 'Salderen (prijsplafond)';
        case 'average_market_tariff':
          return 'Verwachte inkoopprijs';
        case 'purchase_costs':
          return 'Inkoopvergoeding';
        case 'feedin_costs_fixed':
          return 'Terugleverkosten';
        case 'feedin_costs_kwh':
          return 'Terugleverkosten per kWh';
        case 'tax+ode':
          if (specItem.from === 0 || specItem.from === 1) {
            return 'Overheidsheffingen';
          }
          const unit = 'kWh';
          return `Overheidsheffingen ${specItem.from}-${specItem.to} ${unit}`;
        default:
          return specItem.type;
      }
    },
    toCurrencyAccuracy(number, accuracy) {
      if (typeof number !== 'number') {
        return null;
      }
      return `€ ${number.toFixed(accuracy)
        .replace('.', ',')}`;
    },
  },
};
</script>
